// src/store/stream/sendPlan.ts
/**
 * .tuMetrics {"tu1": 0.92, "tu2": 0.94, "tu3": 5, "notes": "Pure planners for sending a stream message: which humans get a mention notification and what it says. No React, no supabase — node-testable", "tuVersion": "2.5"}
 * .help "Decides who gets a 'you were mentioned' notification when a message is sent: people only (never teams, the company, meetings, agents, or yourself), with a title that names the room."
 */
import type { ChatSession, Comment, InlineShareDraft, User } from '../../types';
import { getShareMediaDisplayDefault } from '../../utils/shareMediaDisplay.ts';

/* ----------------------------------------------------------------------------------------------
 * Carved out of streamStore.addStreamMessage (October 2026, readiness audit phase 2).
 * These are the pure parts of the 1,200-line send path. Side effects (DB insert, feed.addEvent,
 * agent trigger) stay in the store and consume these plans. When fan-out moves server-side
 * (phase 4) the RPC implements the same contract, and these tests become its spec.
 * --------------------------------------------------------------------------------------------*/

export type SendStreamType = Comment['streamType'];

export interface MentionNotification {
  userId: string;
  title: string;
  description: string;
}

export interface MentionPlanInput {
  /** Explicit chips + ids resolved from plain "@text" (users, teams, company, meetings, 'all'). */
  mentions: string[];
  sender: Pick<User, 'id' | 'name'>;
  allUsers: Array<Pick<User, 'id' | 'isAgent'>>;
  teams: Array<{ id: string; name: string }>;
  company?: { id: string } | null;
  sessions: Array<Pick<ChatSession, 'id' | 'streamType' | 'recipientStreamId' | 'name' | 'summary'>>;
  streamType: SendStreamType;
  recipientId: string;
}

/**
 * Which humans are notified that they were mentioned, and with what words.
 *
 * Rules (unchanged from the inline code they replace):
 * - `all` is not a mention here — the composer fans @all out into explicit people.
 * - Team / company / meeting ids are room addresses, not pings; they notify nobody.
 * - Agents are never notified this way (they are invoked by the agent stage).
 * - The sender never notifies themself.
 * - Title/description name the room: meeting name, team name, Company, or Direct Message.
 */
export function planMentionNotifications(input: MentionPlanInput): MentionNotification[] {
  const { mentions, sender, allUsers, teams, company, sessions, streamType, recipientId } = input;

  const meetingIds = new Set(sessions.filter((s) => s.streamType === 'standup_stream').map((s) => s.id));
  const teamIds = new Set(teams.map((t) => t.id));
  const agentIds = new Set(allUsers.filter((u) => u.isAgent).map((u) => u.id));

  const humans: string[] = [];
  const seen = new Set<string>();
  for (const id of mentions) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (id === 'all') continue;
    if (meetingIds.has(id)) continue;
    if (company && id === company.id) continue;
    if (teamIds.has(id)) continue;
    if (agentIds.has(id)) continue;
    if (id === sender.id) continue;
    humans.push(id);
  }
  if (humans.length === 0) return [];

  let title = 'Mentioned in Stream';
  let description = `${sender.name} mentioned you in a stream message`;
  if (streamType === 'standup_stream') {
    const session = sessions.find((s) => s.recipientStreamId === recipientId && s.streamType === 'standup_stream');
    const meetingName = session?.name || session?.summary || 'a meeting';
    title = 'Mentioned in Meeting';
    description = `${sender.name} mentioned you in ${meetingName}`;
  } else if (streamType === 'team_stream') {
    const teamName = teams.find((t) => t.id === recipientId)?.name || 'a team stream';
    title = `Mentioned in ${teamName}`;
    description = `${sender.name} mentioned you in the ${teamName} stream`;
  } else if (streamType === 'company_stream') {
    title = 'Mentioned in Company';
    description = `${sender.name} mentioned you in the Company stream`;
  } else if (streamType === 'direct_message') {
    title = 'Direct Message';
    description = `${sender.name} sent you a direct message`;
  }

  return humans.map((userId) => ({ userId, title, description }));
}

/* ----------------------------------------------------------------------------------------------
 * Feed-event gate
 * --------------------------------------------------------------------------------------------*/

/**
 * Messages to an agent (help gutter, DM with a bot) never create feed events — they would make
 * the Feed page refresh on every chat turn. Everything else may.
 */
export function shouldSkipFeedEvents(input: {
  streamType: SendStreamType;
  recipientId: string;
  tags?: string[];
  content: string;
  allUsers: Array<Pick<User, 'id' | 'isAgent'>>;
}): boolean {
  const isHelpSession =
    Boolean(input.tags?.includes('help-session')) || input.content.includes('🎯 Help session started');
  const toAgent =
    input.streamType === 'direct_message' &&
    input.allUsers.some((u) => u.id === input.recipientId && u.isAgent);
  return isHelpSession || toAgent;
}

/* ----------------------------------------------------------------------------------------------
 * Google Meet started
 * --------------------------------------------------------------------------------------------*/

export interface MeetNotification {
  userId: string;
  title: string;
  description: string;
}

export interface MeetPlanInput {
  streamType: SendStreamType;
  recipientId: string;
  sender: Pick<User, 'id' | 'name' | 'companyId'>;
  sharedItemDetails?: Comment['sharedItemDetails'];
  allUsers: Array<Pick<User, 'id' | 'companyId' | 'team' | 'privateTeams'>>;
  teams: Array<{ id: string; name: string }>;
  sessions: Array<Pick<ChatSession, 'streamType' | 'recipientStreamId' | 'userIds' | 'agentIds'>>;
}

/** Everyone who belongs to the room except the sender. Company team means the whole company. */
export function roomMemberIds(
  input: Pick<MeetPlanInput, 'streamType' | 'recipientId' | 'allUsers' | 'teams' | 'sessions'> & {
    companyId?: string | null;
  },
): string[] {
  const { streamType, recipientId, allUsers, teams, sessions, companyId } = input;
  const company = () => allUsers.filter((u) => u.companyId === companyId).map((u) => u.id);
  switch (streamType) {
    case 'direct_message':
      return [recipientId];
    case 'standup_stream': {
      const s = sessions.find((x) => x.streamType === 'standup_stream' && x.recipientStreamId === recipientId);
      return s ? [...(s.userIds || []), ...(s.agentIds || [])] : [];
    }
    case 'team_stream': {
      const team = teams.find((t) => t.id === recipientId);
      if (!team) return [];
      if (team.name === 'Company') return company();
      return allUsers
        .filter((u) => u.team === team.id || u.privateTeams?.includes(team.id))
        .map((u) => u.id);
    }
    case 'company_stream':
      return company();
    default:
      return [];
  }
}

/** A message carrying a Meet share tells everyone in the room (but the starter) that it began. */
export function planMeetNotifications(input: MeetPlanInput): MeetNotification[] {
  const d = input.sharedItemDetails;
  if (!d || (d.type as string) !== 'meet' || !d.url) return [];
  const members = roomMemberIds({ ...input, companyId: input.sender.companyId });
  const seen = new Set<string>();
  const out: MeetNotification[] = [];
  for (const userId of members) {
    if (userId === input.sender.id || seen.has(userId)) continue;
    seen.add(userId);
    out.push({
      userId,
      title: 'Google Meet Started',
      description: `${input.sender.name} started a Google Meet: ${d.title || 'Currents Meet'}`,
    });
  }
  return out;
}

/* ----------------------------------------------------------------------------------------------
 * Shares attached to a message
 * --------------------------------------------------------------------------------------------*/

/** How `create-shared-item` addresses the room this message lands in. */
export function shareRecipientType(streamType: SendStreamType): 'user' | 'team' | 'company' {
  if (streamType === 'team_stream') return 'team';
  if (streamType === 'company_stream') return 'company';
  return 'user';
}

/** The message-side preview for a share the composer already resolved (inline draft). */
export function inlineShareDetails(
  draft: InlineShareDraft,
  detectType: (url: string) => InlineShareDraft['type'],
): NonNullable<Comment['sharedItemDetails']> {
  return {
    title: draft.title || draft.url,
    description: draft.description ?? undefined,
    url: draft.url,
    type: draft.type || detectType(draft.url),
    tags: draft.tags ?? [],
    urlMetadata: draft.urlMetadata ?? undefined,
    driveFileMetadata: draft.driveFileMetadata ?? undefined,
    displaySize: draft.displaySize ?? getShareMediaDisplayDefault(),
  };
}
